// src/components/Qbus/QbusOutputCard.jsx
// Individual output card with state visualization and controls
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Switch,
  Slider,
  Chip,
  Tooltip,
  CircularProgress,
  Collapse,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel
} from '@mui/material';
import {
  Tune as DimIcon,
  Palette as ColorIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  ExpandMore as ExpandIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { getIconComponent } from '../../pages/QbusOutputTypeManagement/utils/iconMapping';

const QbusOutputCard = ({
  output,
  folders = [],
  onSendCommand,
  onUpdateName,
  onUpdateOutput,
  onViewHistory,
  isAdmin,
  canEdit,
  disabled
}) => {
  const { t } = useThemeSettings();
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedName, setEditedName] = useState(output.friendlyName || '');
  const [dimValue, setDimValue] = useState(output.current_value?.value || 0);

  // Determine current state
  const currentValue = output.current_value?.value;
  const isOn = typeof currentValue === 'boolean' ? currentValue : (currentValue > 0);
  const outputType = output.outputType;

  // Check if output is locked (security-sensitive)
  const isLocked = outputType?.isLocked && !isAdmin;

  // Get capabilities
  const supportsOnOff = outputType?.supportsOnOff !== false;
  const supportsDim = outputType?.supportsDim || false;
  const supportsColor = outputType?.supportsColor || false;

  // Handle on/off toggle
  const handleToggle = async () => {
    if (isLocked || disabled || sending) return;

    setSending(true);
    try {
      await onSendCommand(output.id, !isOn);
    } finally {
      setSending(false);
    }
  };

  // Handle dim change
  const handleDimChange = async (event, newValue) => {
    setDimValue(newValue);
  };

  const handleDimCommit = async (event, newValue) => {
    if (isLocked || disabled || sending) return;

    setSending(true);
    try {
      await onSendCommand(output.id, newValue);
    } finally {
      setSending(false);
    }
  };

  // Handle name edit
  const handleSaveName = async () => {
    if (editedName !== (output.friendlyName || '')) {
      await onUpdateName(output.id, editedName);
    }
    setEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedName(output.friendlyName || '');
    setEditing(false);
  };

  // Handle folder change
  const handleFolderChange = (e) => {
    const value = e.target.value;
    if (onUpdateOutput) {
      onUpdateOutput(output.id, { folder_id: value === '' ? null : value });
    }
  };

  // Resolve icon component from output type
  const Icon = getIconComponent(outputType?.icon);

  return (
    <Card
      sx={{
        height: '100%',
        opacity: isLocked ? 0.6 : 1,
        border: isOn ? '2px solid' : '1px solid',
        borderColor: isOn ? 'primary.main' : 'divider',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: isLocked ? 1 : 4
        }
      }}
    >
      <CardContent sx={{ pb: '8px !important', pt: 2.5 }}>
        {/* Big centered icon */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isOn ? 'primary.main' : 'action.hover',
              transition: 'all 0.3s ease-in-out',
              position: 'relative',
            }}
          >
            <Icon
              sx={{
                fontSize: 36,
                color: isOn ? 'primary.contrastText' : 'text.disabled',
                transition: 'color 0.3s ease-in-out',
              }}
            />
            {sending && (
              <CircularProgress
                size={76}
                thickness={2}
                sx={{
                  position: 'absolute',
                  top: -2,
                  left: -2,
                  color: 'primary.main',
                }}
              />
            )}
          </Box>
        </Box>

        {/* Lock badge */}
        {outputType?.isLocked && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
            <Tooltip title={isAdmin
              ? (t ? t('qbus.output.lockedAdmin') : 'Locked output (admin access)')
              : (t ? t('qbus.output.lockedNoAccess') : 'Admin-only output')
            }>
              <Chip
                icon={<LockIcon />}
                label={isAdmin ? 'Admin' : 'Locked'}
                size="small"
                color={isAdmin ? 'warning' : 'error'}
                variant="outlined"
              />
            </Tooltip>
          </Box>
        )}

        {/* Name */}
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          {editing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                size="small"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSaveName()}
                autoFocus
                fullWidth
              />
              <IconButton size="small" onClick={handleSaveName} color="primary">
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                {output.friendlyName || output.name || output.output_id}
              </Typography>
              {!isLocked && (
                <Tooltip title={t ? t('common.edit') : 'Edit name'}>
                  <IconButton size="small" onClick={() => setEditing(true)}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          <Typography variant="caption" color="text.secondary">
            {outputType?.friendlyName || 'Unknown Type'}
          </Typography>
        </Box>

        {/* Main control: on/off toggle */}
        {supportsOnOff && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
            <Switch
              checked={isOn}
              onChange={handleToggle}
              disabled={isLocked || disabled || sending}
            />
          </Box>
        )}

        {/* Dimmer control */}
        {supportsDim && !isLocked && (
          <Box sx={{ px: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <DimIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {t ? t('qbus.output.brightness') : 'Brightness'}
              </Typography>
              <Typography variant="caption" sx={{ ml: 'auto' }}>
                {dimValue}%
              </Typography>
            </Box>
            <Slider
              value={dimValue}
              onChange={handleDimChange}
              onChangeCommitted={handleDimCommit}
              disabled={isLocked || disabled || sending}
              min={0}
              max={100}
              size="small"
            />
          </Box>
        )}

        {/* Color indicator */}
        {supportsColor && !isLocked && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
            <ColorIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {t ? t('qbus.output.colorControl') : 'RGB Color Control'}
            </Typography>
            {currentValue?.r !== undefined && (
              <Box
                sx={{
                  ml: 1,
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  bgcolor: `rgb(${currentValue.r || 0}, ${currentValue.g || 0}, ${currentValue.b || 0})`,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              />
            )}
          </Box>
        )}

        {/* Footer actions */}
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tooltip title={t ? t('qbus.output.viewHistory') : 'View history'}>
            <IconButton size="small" onClick={() => onViewHistory?.(output)} disabled={isLocked}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s'
            }}
          >
            <ExpandIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Expanded details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" component="div">
              <strong>{t ? t('qbus.output.originalName') : 'Original name'}:</strong> {output.name || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              <strong>{t ? t('qbus.output.outputId') : 'Output ID'}:</strong> {output.output_id}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              <strong>{t ? t('qbus.output.functionBlock') : 'Function block'}:</strong> {output.function_block_type || '-'}
            </Typography>
            {output.last_state_change && (
              <Typography variant="caption" color="text.secondary" component="div">
                <strong>{t ? t('qbus.output.lastChange') : 'Last change'}:</strong>{' '}
                {new Date(output.last_state_change).toLocaleString()}
              </Typography>
            )}

            {/* Folder selector (edit permission required) */}
            {canEdit && folders.length > 0 && (
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Folder</InputLabel>
                <Select
                  value={output.folder_id || ''}
                  onChange={handleFolderChange}
                  label="Folder"
                >
                  <MenuItem value="">
                    <em>None (Unorganized)</em>
                  </MenuItem>
                  {folders.map(folder => (
                    <MenuItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default QbusOutputCard;
